import React, { useEffect, useRef, useState } from "react";
import { Col } from "antd";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import { useTranslation } from "react-i18next";
import { Loading } from "components";

import { FeverActionCreators } from "redux/rootActions";
import { selectIsLoading } from "redux/bodyTemperature/charts/chartStates";

import { DownloadIcon, CloseIcon, NoDataIcon } from "icons";
import { generateImg } from "utils";

import {
  BoxItem,
  BoxHeader,
  BoxTitle,
  LightDivider,
  BoxContent,
  WrapperLegendLineChart,
  Dot,
  WrapperChart,
  ContainerChart,
  CheckboxStyled2,
  WrapperLoadingChart,
  WrapperCloseIcon,
  WrapperNoData,
} from "../Dashboard.style";
import { useDispatch, useSelector } from "react-redux";

interface TooltipItem {
  dot: any;
  label: string;
}

interface BarChartProps {
  data: any;
  checkedList: boolean[];
}

const chartItems = [
  {
    id: 0,
    label: "no-heat",
    color: "#2AC769",
  },
  {
    id: 1,
    label: "slight-fever",
    color: "#EBC300",
  },
  {
    id: 2,
    label: "top-fever",
    color: "#FB4E4E",
  },
  {
    id: 3,
    label: "not-attempt",
    color: "#666666",
  },
];

const returnItem = (name: string) => {
  let item: TooltipItem = {
    dot: <></>,
    label: "",
  };
  switch (name) {
    case "noHeat":
      item = {
        dot: <Dot style={{ background: "#2AC769" }} />,
        label: "no-heat",
      };
      break;
    case "lowFever":
      item = {
        dot: <Dot style={{ background: "#EBC300" }} />,
        label: "slight-fever",
      };
      break;
    case "fever":
      item = {
        dot: <Dot style={{ background: "#FB4E4E" }} />,
        label: "top-fever",
      };
      break;
    case "notAttempt":
      item = {
        dot: <Dot style={{ background: "#666666" }} />,
        label: "not-attempt",
      };
      break;
  }

  return item;
};

const CustomizedXAxisTick = (props: any) => {
  const { x, y, payload } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-10} y={0} dy={18} fill="#3B3B3B">
        {payload.value}
      </text>
    </g>
  );
};

const CustomizedYAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "body-temperature",
  });
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={3} fill="#3B3B3B" textAnchor="end">
        {payload.value} {t("people")}
      </text>
    </g>
  );
};

const BarChartCustom = ({ data, checkedList }: BarChartProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "body-temperature",
  });

  const renderTooltipContent = (props: any) => {
    const { payload, label } = props;

    return (
      <div className="customized-tooltip-content">
        <p className="label">{label}</p>
        {payload && (
          <div className="list">
            {payload.map((entry: any, index: any) => (
              <div key={`item-${index}`} className="tooltip-item-wrapper">
                <div className="item-left">
                  {returnItem(entry.name)?.dot}
                  <span>{t(returnItem(entry.name)?.label)}</span>
                </div>
                <div className="item-right">
                  <span>
                    {entry.value}
                    {t("people")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <ContainerChart>
      <ResponsiveContainer width="100%" height={178}>
        <BarChart
          margin={{
            top: 10,
            right: 20,
            left: 0,
            bottom: 0,
          }}
          data={data}
        >
          <CartesianGrid
            vertical={false}
            strokeDasharray="2 2"
            stroke="#CCCCCC"
          />
          <XAxis
            dy={4}
            tickLine={false}
            dataKey="date"
            minTickGap={-100}
            tick={<CustomizedXAxisTick />}
            stroke="#CCCCCC"
          />
          <YAxis
            type="number"
            tickLine={false}
            interval={0}
            width={55}
            tick={<CustomizedYAxisTick />}
            stroke="#CCCCCC"
            minTickGap={200}
          />
          {checkedList[0] && (
            <Bar
              dataKey="noHeat"
              barSize={44}
              stackId="noHeat"
              fill="#2AC769"
            />
          )}
          {checkedList[1] && (
            <Bar
              dataKey="lowFever"
              stackId="noHeat"
              fill="#EBC300"
              barSize={44}
            />
          )}
          {checkedList[2] && (
            <Bar dataKey="fever" barSize={44} stackId="noHeat" fill="#FB4E4E" />
          )}
          {checkedList[3] && (
            <Bar
              dataKey="notAttempt"
              barSize={44}
              stackId="noHeat"
              fill="#666666"
            />
          )}
          <Tooltip content={renderTooltipContent} />
        </BarChart>
      </ResponsiveContainer>
    </ContainerChart>
  );
};

const Fever = ({ data, handleCloseChart }: any) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "body-temperature",
  });

  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading);

  const imgRef = useRef<any>();
  const [checkedList, setCheckedList] = useState<boolean[]>([
    true,
    true,
    true,
    true,
  ]);

  const onChangeCheckbox = (index: any) => {
    const tempCheckedList = [...checkedList];
    tempCheckedList[index] = !tempCheckedList[index];
    setCheckedList(tempCheckedList);
  };

  useEffect(() => {
    dispatch(
      FeverActionCreators.getFeverAction({
        aggregate_date_to: "2022-04-05",
      })
    );
  }, []);

  return (
    // <Col xs={24} md={24} xl={12} >
    <BoxItem ref={imgRef}>
      <WrapperCloseIcon>
        <CloseIcon
          fill="#2F8CAE"
          height="14px"
          width="14px"
          onClick={() => handleCloseChart("5")}
        />
      </WrapperCloseIcon>
      <BoxHeader>
        <BoxTitle>{t("check-attend-status")}</BoxTitle>
        <DownloadIcon
          fill="#3B3B3B"
          style={{ marginTop: 4, cursor: "pointer" }}
          onClick={(event: any) => {
            if (!event.detail || event.detail == 1) {
              generateImg("fever", imgRef.current);
            }
          }}
        />
      </BoxHeader>
      <LightDivider />
      {isLoading ? (
        <WrapperLoadingChart>
          <div className="container">
            <Loading width={70} height={70} />
          </div>
        </WrapperLoadingChart>
      ) : data.length > 0 ? (
        <BoxContent>
          <WrapperChart>
            <ContainerChart>
              <div className="icon-descriptions">
                {chartItems && (
                  <div
                    className="icon-descriptions-stacked"
                    style={{ display: "flex", height: "max-content" }}
                  >
                    {chartItems.map((item: any) => (
                      <div className="description" key={item.id}>
                        <CheckboxStyled2
                          checked={checkedList[item.id]}
                          onChange={() => onChangeCheckbox(item.id)}
                        />
                        <WrapperLegendLineChart>
                          <Dot
                            style={{ background: item.color, marginTop: 0 }}
                          />
                          <p>{t(item.label)}</p>
                        </WrapperLegendLineChart>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ContainerChart>
            <BarChartCustom data={data} checkedList={checkedList} />
          </WrapperChart>
        </BoxContent>
      ) : (
        <WrapperNoData>
          <NoDataIcon width={140} height={140} />
        </WrapperNoData>
      )}
    </BoxItem>
    // </Col>
  );
};

export default Fever;
